// Wrapper fetch — injecte l'access token, gère la rotation sur 401,
// et remonte une ApiError structurée pour l'UI.
//
// L'idée : toutes les pages appellent `api.get`/`api.post` etc. et n'ont
// jamais à se soucier du token. La rotation est transparente côté React.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly body: unknown,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

type Fetcher = typeof fetch;

type AuthAccessors = {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  // Callback lorsque le refresh réussit — le store met à jour ses tokens.
  onRefreshed: (payload: {
    access_token: string;
    expires_at: string;
    refresh_token: string;
  }) => void;
  // Callback lorsque le refresh échoue → logout.
  onAuthExpired: () => void;
};

let _auth: AuthAccessors | null = null;
let _overrideFetch: Fetcher | null = null;

/** Retourne le fetch courant — globalThis.fetch pour pouvoir être stubbé en test. */
function _fetch(...args: Parameters<Fetcher>): ReturnType<Fetcher> {
  return (_overrideFetch ?? globalThis.fetch)(...args);
}

export function configureApiClient(auth: AuthAccessors, f?: Fetcher): void {
  _auth = auth;
  _overrideFetch = f ?? null;
}

/**
 * Une seule promesse de refresh en cours partagée entre tous les appels
 * pour éviter d'en déclencher plusieurs en parallèle quand plusieurs
 * requêtes se prennent un 401 en même temps.
 */
let _refreshing: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  if (!_auth) return null;
  const refresh = _auth.getRefreshToken();
  if (!refresh) return null;

  if (_refreshing) return _refreshing;

  _refreshing = (async () => {
    try {
      const resp = await _fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!resp.ok) {
        _auth?.onAuthExpired();
        return null;
      }
      const data = (await resp.json()) as {
        access_token: string;
        expires_at: string;
        refresh_token: string;
      };
      _auth?.onRefreshed(data);
      return data.access_token;
    } catch {
      _auth?.onAuthExpired();
      return null;
    } finally {
      _refreshing = null;
    }
  })();

  return _refreshing;
}

type RequestInitWithJson = Omit<RequestInit, "body"> & {
  json?: unknown;
};

async function request<T>(
  path: string,
  init: RequestInitWithJson = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const mergedHeaders = new Headers(headers);
  if (json !== undefined) {
    mergedHeaders.set("Content-Type", "application/json");
  }
  const access = _auth?.getAccessToken();
  if (access) {
    mergedHeaders.set("Authorization", `Bearer ${access}`);
  }

  const body = json !== undefined ? JSON.stringify(json) : undefined;
  let resp = await _fetch(path, { ...rest, headers: mergedHeaders, body });

  // 401 → tentative de refresh une seule fois, puis retry.
  if (resp.status === 401 && _auth) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      mergedHeaders.set("Authorization", `Bearer ${newAccess}`);
      resp = await _fetch(path, { ...rest, headers: mergedHeaders, body });
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const payload = await resp
    .json()
    .catch(() => ({ detail: resp.statusText })) as {
    detail?: string;
    [k: string]: unknown;
  };

  if (!resp.ok) {
    const detail =
      typeof payload.detail === "string"
        ? payload.detail
        : `Erreur HTTP ${resp.status}`;
    // Session expirée côté backend : tenant perdu → logout forcé vers /login.
    if (
      _auth &&
      typeof payload.detail === "string" &&
      payload.detail.toLowerCase().includes("aucun tenant")
    ) {
      _auth.onAuthExpired();
    }
    throw new ApiError(resp.status, detail, payload);
  }

  return payload as T;
}

/**
 * Variante binaire de `request` — même flow d'auth + 401 refresh, mais
 * retourne { blob, filename } au lieu de parser du JSON. Utilisé pour les
 * exports CSV/XLSX qui doivent être téléchargés par le navigateur.
 */
async function requestBlob(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const mergedHeaders = new Headers();
  const access = _auth?.getAccessToken();
  if (access) {
    mergedHeaders.set("Authorization", `Bearer ${access}`);
  }

  let resp = await _fetch(path, { method: "GET", headers: mergedHeaders });

  if (resp.status === 401 && _auth) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      mergedHeaders.set("Authorization", `Bearer ${newAccess}`);
      resp = await _fetch(path, { method: "GET", headers: mergedHeaders });
    }
  }

  if (!resp.ok) {
    // Le corps peut être du JSON (erreur applicative) ou n'importe quoi —
    // on essaie JSON puis texte pour remonter un message utile.
    const payload = await resp
      .json()
      .catch(() => ({ detail: resp.statusText })) as { detail?: string };
    throw new ApiError(
      resp.status,
      typeof payload.detail === "string"
        ? payload.detail
        : `Erreur HTTP ${resp.status}`,
      payload,
    );
  }

  const disposition = resp.headers.get("Content-Disposition");
  const filename = disposition ? parseFilename(disposition) : null;
  const blob = await resp.blob();
  return { blob, filename };
}

/** Parse `attachment; filename="factures-2026-04-23.csv"` → la valeur. */
function parseFilename(disposition: string): string | null {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Variante multipart/form-data — même flow d'auth + 401 refresh, mais
 * le body est un `FormData` (pour l'upload de CSV de relevé bancaire par
 * ex.). On ne fixe PAS le `Content-Type` : le navigateur l'ajoute avec la
 * bonne boundary.
 */
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const mergedHeaders = new Headers();
  const access = _auth?.getAccessToken();
  if (access) {
    mergedHeaders.set("Authorization", `Bearer ${access}`);
  }

  let resp = await _fetch(path, {
    method: "POST",
    headers: mergedHeaders,
    body: form,
  });

  if (resp.status === 401 && _auth) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      mergedHeaders.set("Authorization", `Bearer ${newAccess}`);
      resp = await _fetch(path, {
        method: "POST",
        headers: mergedHeaders,
        body: form,
      });
    }
  }

  if (resp.status === 204) {
    return undefined as T;
  }

  const payload = await resp
    .json()
    .catch(() => ({ detail: resp.statusText })) as {
    detail?: string;
    [k: string]: unknown;
  };

  if (!resp.ok) {
    const detail =
      typeof payload.detail === "string"
        ? payload.detail
        : `Erreur HTTP ${resp.status}`;
    throw new ApiError(resp.status, detail, payload);
  }

  return payload as T;
}

export function getAccessTokenForUpload(): string | null {
  return _auth?.getAccessToken() ?? null;
}
export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "POST", json }),
  put: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "PUT", json }),
  patch: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: "PATCH", json }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  getBlob: (path: string) => requestBlob(path),
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
};
